import { useCallback, useEffect, useState } from "react";
import { Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { RequestDetail } from "./RequestDetail";

export const STATUS = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
  contactada: { label: "Contactada", cls: "bg-sky-100 text-sky-800" },
  cerrada: { label: "Cerrada", cls: "bg-emerald-100 text-emerald-800" },
};

export const fmtDate = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
export const fmtEventDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export const StatusBadge = ({ status }) => <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS[status]?.cls}`}>{STATUS[status]?.label}</span>;

export const RequestsTab = () => {
  const [filter, setFilter] = useState("todas");
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/appointments", { params: filter === "todas" ? {} : { status: filter } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errorText(e)));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const counts = data?.counts || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const filters = [["todas", "Todas", total], ...Object.entries(STATUS).map(([k, v]) => [k, v.label, counts[k] || 0])];

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="requests-filters">
        {filters.map(([k, label, n]) => (
          <button key={k} onClick={() => setFilter(k)} className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${filter === k ? "bg-ink text-white border-ink" : "bg-white hover:border-ink/40"}`} data-testid={`requests-filter-${k}`}>
            {label} <span className="opacity-60">({n})</span>
          </button>
        ))}
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin text-granate" /> : data.items.length === 0 ? (
        <div className="bg-white rounded-2xl border p-12 text-center text-ink/50" data-testid="requests-empty"><Inbox className="w-8 h-8 mx-auto mb-3" />No hay solicitudes</div>
      ) : (
        <div className="bg-white rounded-2xl border divide-y" data-testid="requests-list">
          <div className="hidden md:grid grid-cols-[1.2fr_1.4fr_1.2fr_1fr_0.6fr_0.8fr] gap-3 px-5 py-3 text-xs uppercase tracking-wider text-ink/50">
            <span>Recibida</span><span>Nombre</span><span>Servicio</span><span>Fecha evento</span><span>Invitados</span><span>Estado</span>
          </div>
          {data.items.map((a) => (
            <button key={a.id} onClick={() => setOpenId(a.id)} className="w-full text-left px-5 py-4 hover:bg-blush/30 transition-colors grid grid-cols-2 md:grid-cols-[1.2fr_1.4fr_1.2fr_1fr_0.6fr_0.8fr] gap-x-3 gap-y-1 text-sm items-center" data-testid={`request-row-${a.id}`}>
              <span className="text-ink/60 text-xs md:text-sm order-3 md:order-none">{fmtDate(a.created_at)}</span>
              <span className="font-medium order-1 md:order-none">{a.name}</span>
              <span className="order-4 md:order-none text-ink/80">{a.service}</span>
              <span className="hidden md:block text-ink/80">{fmtEventDate(a.event_date)}</span>
              <span className="hidden md:block text-ink/80">{a.guests ?? "—"}</span>
              <span className="order-2 md:order-none justify-self-end md:justify-self-start"><StatusBadge status={a.status} /></span>
            </button>
          ))}
        </div>
      )}
      <RequestDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
};
